import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useApi } from '../../../utils/api';
import { useToast } from '../../../context/ToastContext';
import styles from './Patrols.module.css';
import PatrolDetailModal from './PatrolDetailModal';

const SORT_OPTIONS = [
  { value: 'status',   label: 'Status (most overdue first)' },
  { value: 'area',     label: 'Area (A–Z)' },
  { value: 'priority', label: 'Priority' },
  { value: 'type',     label: 'Type' },
];

const MAX_CHECK_NOTE_LENGTH = 255;

/** Returns { label, className } based on last_checked datetime string */
function getCheckStatus(lastChecked) {
  if (!lastChecked) {
    return { label: 'Never checked', className: styles.statusRed };
  }
  const now = Date.now();
  const then = new Date(lastChecked.replace(' ', 'T')).getTime();
  const hoursAgo = (now - then) / 3600000;

  if (hoursAgo <= 48) return { label: `Checked ${formatAgo(hoursAgo)}`, className: styles.statusGreen };
  if (hoursAgo <= 168) return { label: `Checked ${formatAgo(hoursAgo)}`, className: styles.statusYellow };
  return { label: `Last checked ${formatAgo(hoursAgo)}`, className: styles.statusRed };
}

function formatAgo(hoursAgo) {
  if (hoursAgo < 1) return 'just now';
  if (hoursAgo < 24) return `${Math.round(hoursAgo)}h ago`;
  return `${Math.round(hoursAgo / 24)}d ago`;
}

function sortRequests(requests, sortBy) {
  const copy = [...requests];
  switch (sortBy) {
    case 'area':
      return copy.sort((a, b) => {
        const aArea = [a.city, a.address].filter(Boolean).join(' ').toLowerCase();
        const bArea = [b.city, b.address].filter(Boolean).join(' ').toLowerCase();
        return aArea < bArea ? -1 : aArea > bArea ? 1 : 0;
      });
    case 'priority': {
      const order = { high: 0, normal: 1, low: 2 };
      return copy.sort((a, b) => (order[a.priority] ?? 1) - (order[b.priority] ?? 1));
    }
    case 'type':
      return copy.sort((a, b) => (a.type_name || '').localeCompare(b.type_name || ''));
    case 'status':
    default:
      // Never-checked first, then oldest last_checked first, then priority
      return copy.sort((a, b) => {
        const priorityOrder = { high: 0, normal: 1, low: 2 };

        if (!a.last_checked && !b.last_checked) {
          return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
        }
        if (!a.last_checked) return -1;
        if (!b.last_checked) return 1;

        const delta = new Date(a.last_checked) - new Date(b.last_checked);
        if (delta !== 0) return delta;

        return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
      });
  }
}

function getMapsUrl(address, city) {
  const query = [address, city].filter(Boolean).join(', ').trim();
  if (!query) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function Patrols() {
  const api = useApi();
  const { pushToast } = useToast();
  const [checkInNotes, setCheckInNotes]       = useState({});
  const [checkingIn, setCheckingIn]           = useState(null);
  const [patrolRequests, setPatrolRequests]   = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [recentChecks, setRecentChecks]       = useState([]);
  const [checksLoading, setChecksLoading]     = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('status');
  const [areaFilter, setAreaFilter] = useState('');
  const [detailModal, setDetailModal]         = useState(null);   // {request, checks} or null
  const [detailLoading, setDetailLoading]     = useState(null);   // request id being loaded
  const pollingRef = useRef(null);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const data = await api.getPatrolRequests();
      setPatrolRequests(data.requests || []);
    } catch (err) {
      console.error('Failed to load patrol requests:', err);
    } finally {
      setRequestsLoading(false);
    }
  }, [api]);

  const loadRecentChecks = useCallback(async () => {
    try {
      const data = await api.recentPatrolChecks();
      // API returns rows sorted DESC (most recent first) already
      setRecentChecks((data.rows || []).slice(0, 30));
    } catch (err) {
      console.error('Failed to load recent checks:', err);
    } finally {
      setChecksLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadRequests();
    loadRecentChecks();

    // Poll recent checks every 20s
    pollingRef.current = setInterval(loadRecentChecks, 20000);
    return () => clearInterval(pollingRef.current);
  }, []);

  const handleCheckIn = async (patrolId) => {
    setCheckingIn(patrolId);
    setError(null);
    try {
      const note = (checkInNotes[patrolId] || '').slice(0, MAX_CHECK_NOTE_LENGTH);
      await api.patrolCheckIn(patrolId, note);
      setCheckInNotes((prev) => ({ ...prev, [patrolId]: '' }));
      pushToast('Check-in recorded.', 'success');
      // Reload both — the updated last_checked will push this request to bottom in "status" sort
      await Promise.all([loadRequests(), loadRecentChecks()]);
    } catch (err) {
      const message = err.message || 'Check-in failed';
      setError(message);
      pushToast(message, 'error');
    } finally {
      setCheckingIn(null);
    }
  };

  const openDetail = useCallback(async (requestId) => {
    setDetailLoading(requestId);
    try {
      const data = await api.getPatrolDetail(requestId);
      setDetailModal(data);
    } catch (err) {
      pushToast('Could not load patrol details.', 'error');
    } finally {
      setDetailLoading(null);
    }
  }, [api, pushToast]);

  const patrolAreas = useMemo(() => (
    Array.isArray(window.LEP?.patrol_city_filters)
      ? window.LEP.patrol_city_filters.filter((area) => area?.label && area?.city)
      : []
  ), []);
  const cityMatches = useCallback((city) => {
    if (!areaFilter) return true;
    const requestCity = String(city || '').trim().toLowerCase();
    return areaFilter.split(',').map((configuredCity) => configuredCity.trim().toLowerCase()).filter(Boolean).includes(requestCity);
  }, [areaFilter]);
  const sortedRequests = sortRequests(patrolRequests.filter((request) => cityMatches(request.city)), sortBy);
  const visibleRecentChecks = recentChecks.filter((check) => cityMatches(check.city));

  // Keep patrols visually split by patrol type for quicker scanning.
  const groupedRequests = useMemo(() => {
    const groups = new Map();
    sortedRequests.forEach((req) => {
      const typeName = (req.type_name || 'Patrol').trim() || 'Patrol';
      if (!groups.has(typeName)) groups.set(typeName, []);
      groups.get(typeName).push(req);
    });
    return Array.from(groups.entries()).map(([type, items]) => ({ type, items }));
  }, [sortedRequests]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Patrol Requests</h2>

      {error && <div className={styles.alert + ' ' + styles.alertError} role="alert">{error}</div>}

      {/* Legend */}
      <div className={styles.legend}>
        <span className={styles.legendItem}><span className={`${styles.dot} ${styles.dotGreen}`} /> Checked &lt;2 days</span>
        <span className={styles.legendItem}><span className={`${styles.dot} ${styles.dotYellow}`} /> Checked &lt;1 week</span>
        <span className={styles.legendItem}><span className={`${styles.dot} ${styles.dotRed}`} /> Overdue / never checked</span>
      </div>

      {patrolAreas.length > 0 && (
        <nav className={styles.areaFilters} aria-label="Filter patrols by city">
          <button type="button" className={`${styles.areaButton} ${areaFilter === '' ? styles.areaButtonActive : ''}`} aria-pressed={areaFilter === ''} onClick={() => setAreaFilter('')}>All Areas</button>
          {patrolAreas.map((area, index) => (
            <button key={`${area.city}-${index}`} type="button" className={`${styles.areaButton} ${areaFilter === area.city ? styles.areaButtonActive : ''}`} aria-pressed={areaFilter === area.city} onClick={() => setAreaFilter(area.city)}>{area.label}</button>
          ))}
        </nav>
      )}

      <section className={styles.section}>
        {/* Header row with sort control */}
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Active Patrol Locations</h3>
          <div className={styles.sortControl}>
            <label htmlFor="patrolSort" className={styles.sortLabel}>Sort by</label>
            <select
              id="patrolSort"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={styles.sortSelect}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {requestsLoading && <p className={styles.loading}>Loading...</p>}
        {!requestsLoading && sortedRequests.length === 0 && (
          <p className={styles.empty}>No active patrol requests</p>
        )}

        {!requestsLoading && sortedRequests.length > 0 && (
          <ul className={styles.patrolList} aria-label="Active patrol requests">
            {groupedRequests.map((group) => (
              <React.Fragment key={group.type}>
                <li className={styles.groupHeading}>
                  <span>{group.type}</span>
                  <span>{group.items.length}</span>
                </li>
                {group.items.map((req) => {
                  const status = getCheckStatus(req.last_checked);
                  const mapsUrl = getMapsUrl(req.address, req.city);
                  return (
                    <li key={req.id} className={`${styles.patrolItem} ${status.className}`}>
                      <div className={styles.patrolHeader}>
                        <div className={styles.patrolInfoRow}>
                          <strong className={styles.patrolType}>{req.type_name || 'Patrol'}</strong>
                          {req.priority === 'high' && <span className={styles.priorityHigh}>High</span>}
                          {req.priority === 'low'  && <span className={styles.priorityLow}>Low</span>}
                          <span className={`${styles.statusBadge} ${status.className}Badge`}>{status.label}</span>
                          <button
                            type="button"
                            className={styles.viewBtn}
                            onClick={() => openDetail(req.id)}
                            disabled={detailLoading === req.id}
                          >
                            {detailLoading === req.id ? '…' : 'View'}
                          </button>
                        </div>
                        {(req.address || req.city) && (
                          <div className={styles.patrolAddress}>
                            {mapsUrl ? (
                              <a className={styles.patrolAddressLink} href={mapsUrl} target="_blank" rel="noreferrer">
                                📍 {[req.address, req.city].filter(Boolean).join(', ')}
                              </a>
                            ) : (
                              <>📍 {[req.address, req.city].filter(Boolean).join(', ')}</>
                            )}
                          </div>
                        )}
                        {req.notes && <div className={styles.patrolNotes}>{req.notes}</div>}
                      </div>
                      <div className={styles.checkInRow}>
                        <div className={styles.noteInputBlock}>
                          <input
                            type="text"
                            placeholder="Optional note..."
                            value={checkInNotes[req.id] || ''}
                            maxLength={MAX_CHECK_NOTE_LENGTH}
                            onChange={(e) => setCheckInNotes((prev) => ({ ...prev, [req.id]: e.target.value.slice(0, MAX_CHECK_NOTE_LENGTH) }))}
                            className={styles.noteInput}
                            disabled={checkingIn === req.id}
                          />
                          <div className={`${styles.noteCounter} ${(checkInNotes[req.id] || '').length >= MAX_CHECK_NOTE_LENGTH - 20 ? styles.noteCounterWarn : ''}`}>
                            {(checkInNotes[req.id] || '').length}/{MAX_CHECK_NOTE_LENGTH}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleCheckIn(req.id)}
                          className={styles.button}
                          disabled={checkingIn !== null}
                        >
                          {checkingIn === req.id ? 'Checking In...' : 'Check In'}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </React.Fragment>
            ))}
          </ul>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Recent Check-Ins</h3>
          <button type="button" className={styles.refreshBtn} onClick={loadRecentChecks}>↻ Refresh</button>
        </div>

        {checksLoading && <p className={styles.loading}>Loading...</p>}
        {!checksLoading && visibleRecentChecks.length === 0 && (
          <p className={styles.empty}>No recent check-ins</p>
        )}

        {visibleRecentChecks.length > 0 && (
          <ul className={styles.checksList} aria-label="Recent patrol check-ins">
            {visibleRecentChecks.map((check, idx) => (
              <li key={check.id || idx} className={styles.checkItem}>
                <div className={styles.checkInfo}>
                  <strong>{check.type_name || 'Patrol'}</strong>
                  {(check.address || check.city) && (
                    getMapsUrl(check.address, check.city) ? (
                      <a
                        className={styles.checkAreaLink}
                        href={getMapsUrl(check.address, check.city)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {[check.address, check.city].filter(Boolean).join(', ')}
                      </a>
                    ) : (
                      <span className={styles.checkArea}>{[check.address, check.city].filter(Boolean).join(', ')}</span>
                    )
                  )}
                  <span className={styles.time}>{formatCheckTime(check.created_at)}</span>
                  {check.request_id && (
                    <button
                      type="button"
                      className={styles.viewBtn}
                      onClick={() => openDetail(check.request_id)}
                      disabled={detailLoading === check.request_id}
                    >
                      {detailLoading === check.request_id ? '…' : 'View'}
                    </button>
                  )}
                </div>
                {check.note && <p className={styles.checkNote}>{check.note}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {detailModal && (
        <PatrolDetailModal
          data={detailModal}
          onClose={() => setDetailModal(null)}
        />
      )}
    </div>
  );
}

function formatCheckTime(dtStr) {
  if (!dtStr) return '';
  const d = new Date(dtStr.replace(' ', 'T'));
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const time = d
    .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    .replace(' AM', '\u00A0AM')
    .replace(' PM', '\u00A0PM');
  if (isToday) {
    return time;
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
         time;
}

export default Patrols;
